import React from "react";
import { motion } from "framer-motion";
import { Download, Mail, MessageCircle, ArrowDown } from "lucide-react";
import portfolio from "@/data/portfolio";
import ScheduleCallDialog from "@/components/ScheduleCallDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-20 md:pt-28"
    >
      {/* 1 — Hero image + overlapping profile (now first visual element) */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.99 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <div className="relative overflow-hidden rounded-2xl md:rounded-[3rem] bg-[#F5F5F7]">
            <img
              src={portfolio.hero.backgroundImage}
              alt="Equestrian executive backdrop"
              data-testid="hero-background-image"
              className="w-full h-[26vh] sm:h-[34vh] md:h-[58vh] lg:h-[64vh] object-cover"
              loading="eager"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent" />
          </div>

          {/* Circular profile overlapping the bottom-left */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.45 }}
            className="absolute -bottom-10 sm:-bottom-14 md:-bottom-24 left-5 sm:left-6 md:left-12"
          >
            <img
              src={portfolio.hero.profileImage}
              alt={portfolio.name}
              data-testid="hero-profile-image"
              className="w-24 h-24 sm:w-32 sm:h-32 md:w-48 md:h-48 rounded-full border-[5px] sm:border-[6px] md:border-8 border-white shadow-2xl object-cover"
            />
          </motion.div>
        </motion.div>
      </div>

      {/* 2 — Executive Portfolio text block (now AFTER the image) */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-20 sm:pt-24 md:pt-32 pb-20 md:pb-32">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="inline-block w-10 h-px bg-black/40" />
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-500">
            Executive Portfolio · 2026
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          data-testid="hero-name"
          className="font-serif-display text-[2.75rem] sm:text-6xl lg:text-8xl font-light tracking-tight text-black leading-[0.95]"
        >
          {portfolio.name}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
          className="mt-6 max-w-3xl"
        >
          <p className="text-xl sm:text-2xl text-black font-light">{portfolio.title}</p>
          <p className="mt-2 text-sm sm:text-base text-gray-500 tracking-wide">{portfolio.subtitle}</p>
          <p className="mt-8 text-lg sm:text-xl text-gray-700 font-light leading-relaxed">
            {portfolio.positioning}
          </p>
          {portfolio.intro && (
            <div className="mt-6 space-y-4 text-base sm:text-lg text-gray-600 font-light leading-relaxed">
              {portfolio.intro.split("\n\n").map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          )}
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
          className="mt-10 flex flex-wrap items-center gap-3"
        >
          <a
            href={`${BACKEND_URL}/api/cv/download`}
            data-testid="hero-download-cv"
            className="inline-flex items-center gap-2 bg-black text-white hover:bg-[#1a1a1a] rounded-full px-7 py-3.5 text-sm font-medium transition-colors shadow-lg shadow-black/5"
          >
            <Download className="w-4 h-4" /> Download CV
          </a>
          <ScheduleCallDialog />
          <a
            href="#contact"
            data-testid="hero-contact-me"
            className="inline-flex items-center gap-2 bg-white text-black border border-gray-200 hover:border-black rounded-full px-7 py-3.5 text-sm font-medium transition-all"
          >
            Contact me
          </a>
        </motion.div>

        {/* Socials */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.55 }}
          className="mt-10 flex items-center gap-5 text-gray-500"
        >
          <a href={`mailto:${portfolio.contact.email}`} data-testid="hero-email" className="hover:text-black transition-colors" aria-label="Email">
            <Mail className="w-5 h-5" />
          </a>
          <a href={portfolio.contact.whatsappUrl} target="_blank" rel="noreferrer" data-testid="hero-whatsapp" className="hover:text-black transition-colors" aria-label="WhatsApp">
            <MessageCircle className="w-5 h-5" />
          </a>
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.0, duration: 1 }}
          className="hidden md:flex items-center justify-end mt-16 text-gray-400 text-xs uppercase tracking-[0.22em] gap-2"
        >
          <ArrowDown className="w-3.5 h-3.5" />
          Scroll · Career, ventures and projects
        </motion.div>
      </div>
    </section>
  );
}
